import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { createPlacement, updatePlacement } from '../../../../api/placementApi';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select/dist/declarations/src/types';
import { Placement } from '../../../../types/placement';
import { useScopes } from '../../../../hooks/useScopes';
import { ApiScope } from '../../../../types/api-scopes';
import { Site } from '../../../../types/site';
import { createOption, Option } from '../../../../helpers/selectHelpers';
import selectSizeOptions from '../../../../constants/selectSizeOptions';
import { getSites } from '../../../../api/siteApi';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface AddModalProps {
    placement: Placement|null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Placement) => void;
    onUpdateData: (newData: Placement) => void;
}

const AddEditPlacementModal = ({ placement, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const { hasScope } = useScopes();
    const [loading, setLoading] = useState<boolean>(false);
    const [sites, setSites] = useState<Site[]|null>(null);
    const [selectSiteValue, setSelectSiteValue] = useState<SingleValue<Option>>();
    const [selectSizesValue, setSelectSizesValue] = useState<MultiValue<Option>>([]);
    const [sizeOptions, setSizeOptions] = useState<any>(selectSizeOptions);

    useEffect(() => {
        getSites().then(setSites);
    }, []);

    useEffect(() => {
        if (placement) {
            if (placement.site) {
                setSelectSiteValue(createOption(placement.site.publisher.name, String(placement.site.publisher.id)));
            }

            if (placement.sizes) {
                const selectedSizes = placement.sizes.map((size: string) => createOption(size));
                setSelectSizesValue(selectedSizes);
            }

            if (placement.type) {
                handleForm.setFieldValue('type', placement.type);
            }
        }
    }, [placement]);

    const toggleAddModal = () => {
        toggle();
    };

    const selectSiteOnChange = (newValue: SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
        setSelectSiteValue(newValue);
        handleForm.setFieldValue('siteId', newValue ? Number(newValue.value) : '');
    };

    const selectPlacementsOnChange = (newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>) => {
        setSelectSizesValue(newValue);
        handleForm.setFieldValue('sizes', newValue ? newValue.map((value: Option) => value.value) : []);
    };

    const handleCreate = (inputValue: string) => {
        inputValue = inputValue.toLowerCase();
        const validateInputValue = inputValue.match(/^\d+x\d+$/) === null;
        if (validateInputValue) {
            toast.error('Size inválido');
            return;
        }
        toast.warning('Essa funcionalidade ainda não está disponível, caso precise adicionar um novo size, entre em contato com o suporte.');
        return;
        const newOption = createOption(inputValue);
        setSizeOptions([...sizeOptions, newOption]);
        handleForm.values.sizes.push(inputValue);
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            siteId: placement?.site.id || '',
            name: placement?.name || '',
            // sizes: placement?.sizes || [],
            mediaKitUrl: placement?.mediaKitUrl || '',
            mediaKitFile: '',
            maxRichMediaSizeInKb: placement?.maxRichMediaSizeInKb || '',
            maxImageSizeInKb: placement?.maxImageSizeInKb || '',
            acceptsAudioVideo: placement?.acceptsAudioVideo || false,
            maxMediaSizeInKb: placement?.maxMediaSizeInKb || '',
            maxMediaDurationInSeconds: placement?.maxMediaDurationInSeconds || '',
            refreshInSeconds: placement?.refreshInSeconds || '',
            type: placement?.type || '',
            notes: placement?.notes || '',
        },
        validationSchema: Yup.object({
            siteId: Yup.number().integer().required('Campo obrigatório'),
            name: Yup.string().required('Campo obrigatório'),
            // sizes: Yup.array().min(1, 'Campo obrigatório'),
            mediaKitUrl: Yup.string().nullable().optional().matches(
                /^[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/,
                'URL inválida',
            ),
            mediaKitFile: Yup.mixed().nullable().optional(),
            maxRichMediaSizeInKb: Yup.number().integer().required('Campo obrigatório'),
            maxImageSizeInKb: Yup.number().integer().required('Campo obrigatório'),
            acceptsAudioVideo: Yup.boolean(),
            maxMediaSizeInKb: Yup.number().when(['acceptsAudioVideo', 'type'], {
                is: (acceptsAudioVideo: boolean, type: string) => acceptsAudioVideo || type === 'video' || type === 'audio',
                then: (schema) => schema.integer().required('Campo obrigatório'),
                otherwise: (schema) => schema.integer().optional().nullable(),
            }),
            maxMediaDurationInSeconds: Yup.number().when(['acceptsAudioVideo', 'type'], {
                is: (acceptsAudioVideo: boolean, type: string) => acceptsAudioVideo || type === 'video' || type === 'audio',
                then: (schema) => schema.integer().required('Campo obrigatório'),
                otherwise: (schema) => schema.integer().optional().nullable(),
            }),
            refreshInSeconds: Yup.number().integer().optional().nullable(),
            type: Yup.string().required('Campo obrigatório'),
            notes: Yup.string().nullable().optional(),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            const formData = new FormData();
            for (const [key, value] of Object.entries(values)) {
                if (value === '' || value === 'undefined' || value === null) {
                    continue;
                }

                if (key === 'sizes' && Array.isArray(value)) {
                    value.forEach((size: string) => {
                        formData.append('sizes[]', size);
                    });
                    continue;
                }

                formData.append(key, String(value));
            }

            const file: HTMLInputElement | null = document.querySelector('#mediaKitFile');
            if (file && file.files && file.files[0]) {
                formData.delete('mediaKitFile');
                formData.append('mediaKitFile', file.files[0]);
            }
            if (placement) {
                updatePlacement(placement.id, formData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Bloco de anúncio atualizado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createPlacement(formData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Bloco de anúncio cadastrado com sucesso!');
                        onAddData(res);
                        handleForm.resetForm();
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggleAddModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{placement ? 'Editar' : 'Adicionar'} bloco de anúncio</DialogTitle>
                </DialogHeader>

                <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleForm.handleSubmit();
                    return false;
                }} className="space-y-4">
                    {hasScope(ApiScope.PLACEMENT_WRITE) && (
                        <div className="space-y-2">
                            <Label htmlFor="siteId">
                                Publisher
                            </Label>
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                isClearable={true}
                                isSearchable={true}
                                name="siteId"
                                options={sites ? sites.map((site) => {
                                    return createOption(site?.site || site?.app || '', String(site.id));
                                }) : []}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.siteId && handleForm.errors.siteId) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectSiteOnChange}
                                value={selectSiteValue}
                                isDisabled={!!placement}
                            />
                            {handleForm.touched.siteId && handleForm.errors.siteId && (
                                <p className="text-sm text-red-500">{handleForm.errors.siteId}</p>
                            )}
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="name">
                            Nome do bloco de anúncio
                        </Label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.name}
                        />
                        {handleForm.touched.name && handleForm.errors.name && (
                            <p className="text-sm text-red-500">{handleForm.errors.name}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="sizes">
                            Sizes
                        </Label>
                        <CreatableSelect
                            className="basic-single"
                            classNamePrefix="select"
                            isMulti
                            isClearable={true}
                            isSearchable={true}
                            onCreateOption={handleCreate}
                            name="sizes"
                            styles={{
                                control: (baseStyles) => ({
                                    ...baseStyles,
                                    borderColor: !!(handleForm.touched.sizes && handleForm.errors.sizes) ? 'red' : '#ced4da',
                                }),
                            }}
                            options={sizeOptions}
                            onChange={selectPlacementsOnChange}
                            value={selectSizesValue}
                        />
                        {handleForm.touched.sizes && handleForm.errors.sizes && (
                            <p className="text-sm text-red-500">{handleForm.errors.sizes}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="mediaKitUrl">
                                    Mídia Kit - URL
                                </Label>
                                {placement?.mediaKitUrl && (
                                    <a href={placement.mediaKitUrl} target="_blank" rel="noreferrer">
                                        <i className={'ri-external-link-line'}></i>
                                    </a>
                                )}
                                <Input
                                    type="text"
                                    id="mediaKitUrl"
                                    name="mediaKitUrl"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.mediaKitUrl}
                                />
                                {handleForm.touched.mediaKitUrl && handleForm.errors.mediaKitUrl && (
                                    <p className="text-sm text-red-500">{handleForm.errors.mediaKitUrl}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="mediaKitFile">
                                    Mídia Kit - Arquivo PDF
                                </Label>
                                {placement?.mediaKitFile && (
                                    <a href={placement.mediaKitFile} target="_blank" rel="noreferrer">
                                        <i className={'ri-external-link-line'}></i>
                                    </a>
                                )}
                                <Input
                                    type="file"
                                    id="mediaKitFile"
                                    name="mediaKitFile"
                                    accept=".pdf"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.mediaKitFile}
                                />
                                {handleForm.touched.mediaKitFile && handleForm.errors.mediaKitFile && (
                                    <p className="text-sm text-red-500">{handleForm.errors.mediaKitFile}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="flex items-center space-x-2">
                                <input
                                    name="type"
                                    type="radio"
                                    id="type_display"
                                    value="display"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === 'display'}
                                    disabled={!!placement}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="type_display" className="cursor-pointer">
                                    Display
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <input
                                    name="type"
                                    type="radio"
                                    id="type_video"
                                    value="video"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === 'video'}
                                    disabled={!!placement}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="type_video" className="cursor-pointer">
                                    Vídeo
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <input
                                    name="type"
                                    type="radio"
                                    id="type_audio"
                                    value="audio"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === 'audio'}
                                    disabled={!!placement}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="type_audio" className="cursor-pointer">
                                    Audio
                                </Label>
                            </div>
                        </div>
                        {handleForm.touched.type && handleForm.errors.type && (
                            <p className="text-sm text-red-500">{handleForm.errors.type}</p>
                        )}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold mb-4">Configurações</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="maxRichMediaSizeInKb">
                                    Size máximo de Rich Media (Kb)
                                </Label>
                                <Input
                                    type="text"
                                    id="maxRichMediaSizeInKb"
                                    name="maxRichMediaSizeInKb"
                                    placeholder="150"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.maxRichMediaSizeInKb}
                                />
                                {handleForm.touched.maxRichMediaSizeInKb && handleForm.errors.maxRichMediaSizeInKb && (
                                    <p className="text-sm text-red-500">{handleForm.errors.maxRichMediaSizeInKb}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="maxImageSizeInKb">
                                    Size máximo da imagem (Kb)
                                </Label>
                                <Input
                                    type="text"
                                    id="maxImageSizeInKb"
                                    name="maxImageSizeInKb"
                                    placeholder="40"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.maxImageSizeInKb}
                                />
                                {handleForm.touched.maxImageSizeInKb && handleForm.errors.maxImageSizeInKb && (
                                    <p className="text-sm text-red-500">{handleForm.errors.maxImageSizeInKb}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="maxMediaSizeInKb">
                                Size máximo do vídeo/audio (Kb)
                            </Label>
                            <Input
                                type="text"
                                id="maxMediaSizeInKb"
                                name="maxMediaSizeInKb"
                                placeholder="50000"
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                value={handleForm.values.maxMediaSizeInKb}
                            />
                            {handleForm.touched.maxMediaSizeInKb && handleForm.errors.maxMediaSizeInKb && (
                                <p className="text-sm text-red-500">{handleForm.errors.maxMediaSizeInKb}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="maxMediaDurationInSeconds">
                                Tempo máximo de vídeo/audio (segundos)
                            </Label>
                            <Input
                                type="text"
                                id="maxMediaDurationInSeconds"
                                name="maxMediaDurationInSeconds"
                                placeholder="30"
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                value={handleForm.values.maxMediaDurationInSeconds}
                            />
                            {handleForm.touched.maxMediaDurationInSeconds && handleForm.errors.maxMediaDurationInSeconds && (
                                <p className="text-sm text-red-500">{handleForm.errors.maxMediaDurationInSeconds}</p>
                            )}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="refreshInSeconds">
                            Loop / Refresh (segundos)
                        </Label>
                        <Input
                            type="text"
                            id="refreshInSeconds"
                            name="refreshInSeconds"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.refreshInSeconds}
                        />
                        {handleForm.touched.refreshInSeconds && handleForm.errors.refreshInSeconds && (
                            <p className="text-sm text-red-500">{handleForm.errors.refreshInSeconds}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="notes">
                            Observações
                        </Label>
                        <Input
                            type="textarea"
                            id="notes"
                            name="notes"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.notes}
                        />
                        {handleForm.touched.notes && handleForm.errors.notes && (
                            <p className="text-sm text-red-500">{handleForm.errors.notes}</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {placement ? 'Atualizar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditPlacementModal;